import React, { useState, useEffect, useRef } from 'react';
import { callNumber, hangup, toggleMute, subscribe, fmtTime } from '../services/softphone';

const ADMIN_API = 'https://zhylytaban.kz/admin/api';
const SYSTEM_KEY = 'shelter_chat_sync_2026';

const ROLE_COLORS = { 'Приют': '#2f8f4e', 'Фонд': '#8f6a2f', 'Пристройство': '#2f5f8f' };

function fmtNumber(n) {
  const d = (n || '').replace(/\D/g, '');
  if (d.length === 11) return `+${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}`;
  return n || 'номер скрыт';
}

function fmtWhen(s) {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' });
  return sameDay ? `сегодня ${time}` : d.toLocaleDateString('ru', { day: 'numeric', month: 'long' }) + ` ${time}`;
}

export default function VoicemailMessage({ message }) {
  let data = {};
  try { data = JSON.parse(message.content); } catch (e) {}

  const { call_id, caller, caller_name, line_role, started_at, secs } = data;
  const [transcript, setTranscript] = useState(data.transcript || '');
  const [phone, setPhone] = useState({ status: 'idle', number: '', error: '', muted: false, seconds: 0 });
  const [played, setPlayed] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => { setTranscript(data.transcript || ''); }, [data.transcript]);

  useEffect(() => subscribe(setPhone), []);

  // расшифровка иногда дозревает уже после карточки — дотягиваем сами
  useEffect(() => {
    if (transcript || !call_id) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(`${ADMIN_API}/telephony/chat/vm-info/${call_id}?key=${SYSTEM_KEY}`);
        if (r.ok) {
          const info = await r.json();
          if (!stop && info.transcript) setTranscript(info.transcript);
        }
      } catch (e) {}
    };
    const id = setInterval(tick, 15000);
    tick();
    return () => { stop = true; clearInterval(id); };
  }, [call_id, transcript]);

  const audioUrl = `${ADMIN_API}/telephony/chat/vm-audio/${call_id}?key=${SYSTEM_KEY}`;
  const roleColor = ROLE_COLORS[line_role] || '#666';
  const who = caller_name || fmtNumber(caller);
  const canCall = caller && caller.replace(/\D/g, '').length >= 11;
  const busy = phone.status !== 'idle' && phone.status !== 'ended';
  const mine = busy && phone.number === caller;

  function onPlay() {
    if (played || !call_id) return;
    setPlayed(true);
    fetch(`${ADMIN_API}/telephony/chat/vm-read/${call_id}?key=${SYSTEM_KEY}`, { method: 'POST' }).catch(() => {});
  }

  const statusText = {
    connecting: 'Подключаюсь…', calling: 'Набираю номер…', ringing: 'Идут гудки…',
    talking: 'Разговор ' + fmtTime(phone.seconds), ended: 'Звонок завершён', error: phone.error || 'Ошибка',
  }[phone.status];

  return (
    <div style={{
      background: '#fff', border: '1px solid #e3e6ea', borderLeft: `4px solid ${roleColor}`,
      borderRadius: 10, padding: '10px 12px', maxWidth: 520, boxShadow: '0 1px 2px rgba(0,0,0,.05)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 18 }}>📞</span>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{who}</span>
        {line_role && (
          <span style={{
            background: roleColor, color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600,
          }}>{line_role}</span>
        )}
      </div>

      {caller_name && (
        <div style={{ color: '#666', fontSize: 13, marginBottom: 2 }}>{fmtNumber(caller)}</div>
      )}
      <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
        {fmtWhen(started_at)}{secs ? ` · сообщение ${fmtTime(secs)}` : ''}
      </div>

      {/* preload=metadata — чтобы плеер сразу показывал длительность, а не 0:00 */}
      <audio ref={audioRef} src={audioUrl} controls preload="metadata" onPlay={onPlay}
             style={{ width: '100%', height: 36, marginBottom: 8 }} />

      <div style={{
        background: '#f6f7f9', borderRadius: 8, padding: '8px 10px', fontSize: 14,
        lineHeight: 1.45, color: transcript ? '#222' : '#999', whiteSpace: 'pre-wrap', marginBottom: 10,
      }}>
        {transcript
          ? transcript.replace(/\[\d{2}:\d{2}\]\s*/g, '')
          : 'Расшифровка готовится…'}
      </div>

      {!busy && (
        <button
          onClick={() => canCall && callNumber(caller)}
          disabled={!canCall}
          style={{
            background: canCall ? '#2f8f4e' : '#c8ccd0', color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: canCall ? 'pointer' : 'default',
          }}>
          📱 Перезвонить
        </button>
      )}

      {busy && (
        <div style={{
          background: mine ? '#eaf6ee' : '#fff6e6', border: '1px solid ' + (mine ? '#bfe3cb' : '#f0dcb4'),
          borderRadius: 8, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: mine ? 8 : 0 }}>
            {mine ? statusText : `Идёт другой звонок (${fmtNumber(phone.number)})`}
          </div>
          {mine && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={toggleMute} style={{
                background: phone.muted ? '#e6a23c' : '#eef1f4', color: phone.muted ? '#fff' : '#333',
                border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer',
              }}>{phone.muted ? '🔇 Включить микрофон' : '🎤 Заглушить'}</button>
              <button onClick={hangup} style={{
                background: '#d64545', color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Завершить</button>
            </div>
          )}
        </div>
      )}

      {phone.status === 'error' && !busy && (
        <div style={{ color: '#d64545', fontSize: 13, marginTop: 6 }}>{phone.error}</div>
      )}
    </div>
  );
}
